import { requestCodexText } from '../../lib/codexText';
/**
 * PromptBuilder - Phase 1 of Character Builder
 *
 * AI-assisted character concept development:
 * - User enters character description
 * - Codex analyzes and returns suggested tags + cultural context
 * - Auto-rotating cultural insights with fade animation
 * - "Expand" for more suggestions, "Confirm" to proceed
 *
 * Integrated selection for:
 * - Medium (archetype): Film, Manga/Anime, Comic/Cartoon, Game 3D
 * - Character Type (form): Humanoid or Creature
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SparkleIcon,
  CaretRightIcon,
  ArrowsClockwiseIcon,
  CircleNotchIcon,
  PlusIcon,
  CaretLeftIcon,
  CheckIcon,
  CaretDownIcon,
  ImageIcon,
  XIcon,
  UploadIcon,
  UsersIcon
} from '@phosphor-icons/react';
import { analyzeCharacterConcept, CharacterConceptAnalysis, CensorshipType } from '../../lib/geminiCharacterAnalysis';
import { CharacterArchetype, ReferenceImage } from '../../types';
import { CHARACTER_ARCHETYPES } from '../../lib/characterArchetypes';
import { getStylesForArchetype, StyleOption } from '../../lib/characterStyles';
import { generateId } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { uploadToR2 } from '../../lib/storage';
import { useToast } from '../../context/ToastContext';
import { CreditCostBadge } from '../CreditCostBadge';

interface PromptBuilderProps {
  onConfirm: (
    description: string,
    selectedTags: string[],
    analysis: CharacterConceptAnalysis,
    selectedStyle?: StyleOption,
    archetype?: CharacterArchetype,
    referenceImages?: ReferenceImage[]
  ) => void;
  onBack?: () => void;
  initialDescription?: string;
  initialTags?: string[];
  initialStyle?: StyleOption;  // Restore from Supabase builder_session
  archetype?: CharacterArchetype;  // Optional - can be selected in component
  initialReferenceImages?: ReferenceImage[];  // Restore uploaded images
  projectId?: string;  // For R2 temp storage path
  characterId?: string;  // For R2 temp storage path
}

// localStorage key for PromptBuilder state persistence
const PROMPT_BUILDER_LOCAL_KEY = 'ezart_prompt_builder_local';

export default function PromptBuilder({
  onConfirm,
  onBack,
  initialDescription = '',
  initialTags = [],
  initialStyle,
  archetype: initialArchetype,
  initialReferenceImages = [],
  projectId,
  characterId,
}: PromptBuilderProps) {
  const { user } = useAuth();
  const { showToast } = useToast();

  // Restore state from localStorage on mount
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(PROMPT_BUILDER_LOCAL_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved PromptBuilder state:', e);
    }
    return null;
  };

  const savedState = getInitialState();

  // Selection state for medium
  const [selectedArchetype, setSelectedArchetype] = useState<CharacterArchetype | null>(
    savedState?.selectedArchetype || initialArchetype || null
  );
  const [showMediumDropdown, setShowMediumDropdown] = useState(false);
  const mediumDropdownRef = useRef<HTMLDivElement>(null);

  // Get medium metadata for display
  const mediumMeta = selectedArchetype ? CHARACTER_ARCHETYPES[selectedArchetype] : null;
  const availableStyles = selectedArchetype ? getStylesForArchetype(selectedArchetype) : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mediumDropdownRef.current && !mediumDropdownRef.current.contains(event.target as Node)) {
        setShowMediumDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Style carousel state - restore from prop (Supabase), then localStorage, then default
  const [selectedStyle, setSelectedStyle] = useState<StyleOption | null>(() => {
    // Priority 1: Prop from Supabase builder_session (resume flow)
    if (initialStyle) {
      return initialStyle;
    }
    // Priority 2: localStorage (browser refresh)
    if (savedState?.selectedStyleId) {
      const found = availableStyles.find(s => s.id === savedState.selectedStyleId);
      if (found) return found;
    }
    // Priority 3: Default to first available style
    return availableStyles[0] || null;
  });
  const [styleCarouselIndex, setStyleCarouselIndex] = useState(0);
  const styleCarouselRef = useRef<HTMLDivElement>(null);

  // Input state - restore from saved or use initial
  const [description, setDescription] = useState(savedState?.description || initialDescription);

  // Reference images state (up to 3 images)
  // When restoring from database, blob URLs are stale - use base64 as data URL instead
  const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>(() => {
    if (!initialReferenceImages || initialReferenceImages.length === 0) return [];
    return initialReferenceImages.map(img => {
      // If URL is a blob URL (won't work after page reload), use base64 as data URL
      if (img.url?.startsWith('blob:') && img.base64) {
        return {
          ...img,
          url: img.base64.startsWith('data:') ? img.base64 : `data:image/png;base64,${img.base64}`,
        };
      }
      return img;
    });
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload-only mode - skip description, let AI analyze images
  const [uploadOnlyMode, setUploadOnlyMode] = useState(false);
  const [isAnalyzingImages, setIsAnalyzingImages] = useState(false);
  const [imageAnalysisResult, setImageAnalysisResult] = useState<{
    gender: 'male' | 'female' | 'neutral';
    description: string;
    characteristics: string[];
  } | null>(null);

  // Analysis state - restore from saved
  const [analysis, setAnalysis] = useState<CharacterConceptAnalysis | null>(savedState?.analysis || null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [expandCount, setExpandCount] = useState(savedState?.expandCount || 0);

  // Auto-save state to localStorage (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const stateToSave = {
        description,
        selectedArchetype,
        selectedStyleId: selectedStyle?.id,
        analysis,
        expandCount,
      };
      localStorage.setItem(PROMPT_BUILDER_LOCAL_KEY, JSON.stringify(stateToSave));
    }, 300); // Debounce 300ms

    return () => clearTimeout(timeoutId);
  }, [description, selectedArchetype, selectedStyle, analysis, expandCount]);

  // Clear localStorage when confirming (state moves to parent)
  const clearLocalState = () => {
    localStorage.removeItem(PROMPT_BUILDER_LOCAL_KEY);
  };

  // Cultural context rotation
  const [currentContextIndex, setCurrentContextIndex] = useState(0);
  const [contextVisible, setContextVisible] = useState(true);
  const rotationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Focus state for input styling
  const [isFocused, setIsFocused] = useState(false);

  // Auto-rotate cultural context every 8 seconds
  useEffect(() => {
    if (analysis?.culturalContext && analysis.culturalContext.length > 1) {
      rotationIntervalRef.current = setInterval(() => {
        // Fade out
        setContextVisible(false);

        setTimeout(() => {
          // Change context
          setCurrentContextIndex(prev =>
            (prev + 1) % (analysis.culturalContext?.length || 1)
          );
          // Fade in
          setContextVisible(true);
        }, 300); // Wait for fade out animation
      }, 8000); // Rotate every 8 seconds
    }

    return () => {
      if (rotationIntervalRef.current) {
        clearInterval(rotationIntervalRef.current);
      }
    };
  }, [analysis?.culturalContext]);

  // Analyze character concept - only triggered by Expand button
  const handleExpand = useCallback(async () => {
    if (!description.trim() || isAnalyzing) return;

    setExpandCount(prev => prev + 1);
    setIsAnalyzing(true);

    try {
      const result = await analyzeCharacterConcept(
        description,
        expandCount,
        [], // Tags are now embedded in description
        selectedArchetype || undefined,
        selectedStyle?.name,
        selectedStyle?.description
      );
      setAnalysis(result);
      setCurrentContextIndex(0);
      setContextVisible(true);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [description, expandCount, isAnalyzing, selectedArchetype, selectedStyle]);

  const handleTagClick = (tag: string) => {
    // Append tag to description with comma separator
    setDescription(prev => {
      const trimmed = prev.trim();
      if (!trimmed) return tag;
      // Add comma if not already ending with one
      const separator = trimmed.endsWith(',') ? ' ' : ', ';
      const newDescription = trimmed + separator + tag;
      // Don't add if it would exceed the 500 character limit
      if (newDescription.length > 500) return prev;
      return newDescription;
    });
  };

  // Reference image handlers
  const handleImageUpload = useCallback(async (files: FileList | null) => {
    if (!files) return;

    const newImages: ReferenceImage[] = [];
    const maxImages = 3;
    const currentCount = referenceImages.length;
    const availableSlots = maxImages - currentCount;
    const maxFileSize = 25 * 1024 * 1024; // 25MB in bytes

    for (let i = 0; i < Math.min(files.length, availableSlots); i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      // Check file size
      if (file.size > maxFileSize) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        showToast(`Image "${file.name}" is too large (${sizeMB}MB). Maximum size is 25MB.`, 'error');
        continue;
      }

      // Convert to base64 for API calls (and fallback display)
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Remove data URL prefix for API calls
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.readAsDataURL(file);
      });

      // Upload to R2 temp storage for persistent URL
      // Path: character/temp/{projectId}/{characterId}/references/
      let persistentUrl: string;
      try {
        const userId = user?.id || 'anonymous';
        const tempPath = projectId && characterId
          ? `character/temp/${projectId}/${characterId}/references`
          : `character/temp/orphan/${userId}/references`;
        persistentUrl = await uploadToR2(file, tempPath, userId);
        console.log('📤 Reference image uploaded to R2:', persistentUrl);
      } catch (error) {
        console.error('Failed to upload reference image to R2:', error);
        // Skip this image if R2 upload fails - don't use data URL fallback
        // Data URLs bloat the database when saved to builder_session
        continue;
      }

      newImages.push({
        id: generateId(),
        url: persistentUrl,  // Use R2 URL for persistence
        base64,  // Keep base64 for API calls
        thumbnail: persistentUrl,
      });
    }

    setReferenceImages(prev => [...prev, ...newImages]);
  }, [referenceImages.length, user?.id]);

  const handleRemoveImage = useCallback((imageId: string) => {
    setReferenceImages(prev => {
      const updated = prev.filter(img => img.id !== imageId);
      // Note: R2 temp images will be cleaned up by lifecycle policy or when character is finalized
      return updated;
    });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleImageUpload(e.dataTransfer.files);
  }, [handleImageUpload]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Analyze uploaded images to extract gender and characteristics (for upload-only mode)
  // Returns the analysis result for use in confirm handler
  const analyzeUploadedImages = useCallback(async (): Promise<{
    gender: 'male' | 'female' | 'neutral';
    description: string;
    characteristics: string[];
  } | null> => {
    if (referenceImages.length === 0) return null;

    setIsAnalyzingImages(true);
    try {
      // Use the first image with base64 data
      const imageToAnalyze = referenceImages.find(img => img.base64);
      if (!imageToAnalyze?.base64) return null;

      // Prompt follows the same rules as geminiPromptEnhancer - focus ONLY on permanent physical attributes
      const prompt = `Analyze this reference image for character creation.

============================================
⚠️ STRIP OUT (IGNORE COMPLETELY IN YOUR ANALYSIS)
============================================
- Style keywords (anime, photorealistic, 3D, comic, cartoon, cel shaded, etc.)
- Lighting terms (dramatic lighting, soft lighting, studio lighting, etc.)
- Background references (any background, environment, scenery)
- Posture/pose terms (standing, sitting, action pose, T-pose, etc.)
- CLOTHING AND ACCESSORIES (shirts, pants, armor, jewelry, weapons, hats, glasses)
- Camera/composition (close-up, wide shot, angle, zoom, framing)
- Technical terms (8K, high quality, detailed textures, resolution)
- Filters/effects (vignette, blur, grain, accent colors)
- Locations (forest, city, indoors, etc.)

============================================
✅ EXTRACT ONLY PERMANENT PHYSICAL TRAITS
============================================
HUMANOID:
- Face: structure, shape, jawline, cheekbones
- Eyes: color, shape (monolid, almond, round, hooded)
- Nose: shape (broad, narrow, aquiline, button)
- Mouth/lips: shape, fullness
- Ears: shape, size
- Hair: color, style, texture, length
- Skin: tone, texture, freckles, moles, birthmarks
- Body: type, build, physique, proportions
- Tattoos: permanent body art (KEEP these)
- Piercings: permanent body modifications (KEEP these)
- Scars: permanent markings (KEEP these)
- Age appearance

CREATURE/NON-HUMAN:
- Scales, fur, feathers, exoskeleton
- Claws, horns, fangs, tusks
- Wings, tails, tentacles
- Body structure (quadruped/biped/winged)
- Coloring and patterns

============================================
YOUR TASK
============================================
Determine:
1. Gender: Is the character/person male, female, or neutral/ambiguous?
2. Brief description: Describe the character in 20-30 words focusing ONLY on permanent physical traits
   - DO NOT mention what they are wearing (shirts, pants, dresses, armor, etc.)
   - DO NOT mention accessories (jewelry, hats, glasses, weapons, etc.)
   - ONLY describe: skin, body type, face structure, hair, eyes, distinguishing marks
3. Key characteristics: List 3-5 permanent physical features
   - Hair color/style, eye color/shape, skin tone, body build, facial features, scars, tattoos, piercings
   - NO clothing items, NO accessories, NO style keywords

Respond with ONLY valid JSON (no markdown):
{
  "gender": "male" | "female" | "neutral",
  "description": "Brief physical description only - NO clothing, NO accessories",
  "characteristics": ["physical_trait1", "physical_trait2", "physical_trait3"]
}`;

      const text = await requestCodexText(prompt, [imageToAnalyze.base64]);

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        const result = {
          gender: parsed.gender || 'neutral',
          description: parsed.description || 'Character from reference image',
          characteristics: parsed.characteristics || []
        };
        setImageAnalysisResult(result);
        console.log('✅ Image analysis complete:', parsed);
        return result;
      }
      return null;
    } catch (error) {
      console.error('Failed to analyze images:', error);
      return null;
    } finally {
      setIsAnalyzingImages(false);
    }
  }, [referenceImages]);

  // Note: Image analysis is no longer auto-triggered when upload-only mode is enabled
  // Instead, analysis happens when the user clicks Confirm

  // Clear analysis when images are removed
  useEffect(() => {
    if (referenceImages.length === 0) {
      setImageAnalysisResult(null);
    }
  }, [referenceImages.length]);

  const handleConfirm = async () => {
    const canProceed = uploadOnlyMode
      ? (referenceImages.length > 0 && selectedArchetype)
      : (description.trim() && selectedArchetype);

    if (canProceed) {
      // For upload-only mode, analyze images now if not already done
      let effectiveAnalysisResult = imageAnalysisResult;
      if (uploadOnlyMode && referenceImages.length > 0 && !imageAnalysisResult) {
        effectiveAnalysisResult = await analyzeUploadedImages();
      }

      // Clear local storage since state is moving to parent
      clearLocalState();

      // For upload-only mode, use AI-analyzed gender; otherwise infer from description
      const effectiveGender = uploadOnlyMode && effectiveAnalysisResult
        ? effectiveAnalysisResult.gender
        : inferGenderFromDescription(description);

      // Build description from image analysis if in upload-only mode
      const effectiveDescription = uploadOnlyMode && effectiveAnalysisResult
        ? `${effectiveAnalysisResult.description}. ${effectiveAnalysisResult.characteristics.join(', ')}`
        : description;

      // Use existing analysis or create a default one
      const finalAnalysis: CharacterConceptAnalysis = analysis || {
        suggestedTags: effectiveAnalysisResult?.characteristics || [],
        culturalContext: [],
        inferredGender: effectiveGender,
        characterType: 'humanoid',
        censorshipType: effectiveGender === 'female' ? 'female_censored' : 'male_censored'
      };

      // Pass reference images for Codex classification in next phase
      // Strip base64 from referenceImages before passing to parent (will be saved to Supabase)
      // Only keep URLs - base64 should NOT be saved to database
      const imagesForStorage = referenceImages.length > 0
        ? referenceImages.map(img => ({
            id: img.id,
            url: img.url,
            thumbnail: img.thumbnail,
            type: img.type,
            // Explicitly omit base64 and file - these should not go to Supabase
          }))
        : undefined;
      onConfirm(effectiveDescription, [], finalAnalysis, selectedStyle || undefined, selectedArchetype, imagesForStorage);
    }
  };

  // Helper to infer gender from description keywords
  const inferGenderFromDescription = (desc: string): 'male' | 'female' | 'neutral' => {
    const lower = desc.toLowerCase();
    const femaleKeywords = ['female', 'woman', 'girl', 'she', 'her', 'lady', 'queen', 'princess', 'goddess', 'witch', 'sorceress'];
    const maleKeywords = ['male', 'man', 'boy', 'he', 'his', 'king', 'prince', 'god', 'wizard', 'sorcerer'];

    if (femaleKeywords.some(k => lower.includes(k))) return 'female';
    if (maleKeywords.some(k => lower.includes(k))) return 'male';
    return 'neutral';
  };

  // Check if form is complete enough to confirm
  // Can confirm if: (has description OR upload-only with images) AND has archetype selected
  const canConfirm = (
    (description.trim() || (uploadOnlyMode && referenceImages.length > 0))
    && selectedArchetype
  );

  // Style carousel navigation
  const scrollStyleCarousel = (direction: 'left' | 'right') => {
    if (styleCarouselRef.current) {
      const scrollAmount = 200;
      styleCarouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const currentContext = analysis?.culturalContext?.[currentContextIndex] || '';
  // Filter out tags that are already in the description (case-insensitive)
  const descriptionLower = description.toLowerCase();
  const suggestedTags = analysis?.suggestedTags?.filter(tag =>
    !descriptionLower.includes(tag.toLowerCase())
  ) || [];

  return (
    <div className="flex flex-col h-full bg-onyx text-white select-none overflow-y-auto">
      {/* Vertically centered content wrapper */}
      <div className="flex-1 flex flex-col justify-center py-8">
        {/* Centered Content Container */}
        <div className="w-full max-w-4xl mx-auto px-4 space-y-5">
          {/* Cultural Context - Auto-rotating */}
          <div className="min-h-[70px] flex items-center justify-center">
            {currentContext ? (
              <div
                className={`text-center transition-all duration-300 ${contextVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
                  }`}
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <SparkleIcon className="w-4 h-4 text-slate-blue" />
                  <span className="text-xs uppercase tracking-widest text-slate-blue/70">Cultural Context</span>
                </div>
                <p className="text-sm text-gray-400 italic leading-relaxed">
                  "{currentContext}"
                </p>
                {analysis?.culturalContext && analysis.culturalContext.length > 1 && (
                  <div className="flex justify-center gap-1 mt-3">
                    {analysis.culturalContext.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-1.5 h-1.5 rounded-full transition-all ${idx === currentContextIndex
                          ? 'bg-slate-blue w-4'
                          : 'bg-iron'
                          }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : isAnalyzing ? (
              <div className="flex items-center gap-2 text-gray-500">
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
                <span className="text-sm">Analyzing character concept...</span>
              </div>
            ) : null}
          </div>

          {/* Title */}
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-1">
              Describe Your Character
            </h1>
            <p className="text-sm text-gray-500 mb-3">
              Select a medium to get started
            </p>
            {/* Medium Dropdown */}
            <div className="relative inline-block" ref={mediumDropdownRef}>
              <button
                onClick={() => setShowMediumDropdown(!showMediumDropdown)}
                className={`flex items-center gap-2 px-3 py-1.5 border rounded-full transition-all ${selectedArchetype
                  ? 'bg-carbon border-iron/50 hover:border-slate-blue/50'
                  : 'bg-slate-blue/10 border-slate-blue/50 animate-pulse'
                  }`}
              >
                <span className="text-xs text-gray-500">Medium:</span>
                <span className={`text-xs font-medium ${selectedArchetype ? 'text-slate-blue-soft' : 'text-slate-blue'}`}>
                  {mediumMeta?.badge || 'Select...'}
                </span>
                <CaretDownIcon className={`w-3 h-3 text-gray-500 transition-transform ${showMediumDropdown ? 'rotate-180' : ''}`} />
              </button>
              {/* Medium Dropdown Menu */}
              {showMediumDropdown && (
                <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 min-w-[160px] bg-carbon border border-iron rounded-xl shadow-xl overflow-hidden">
                  {(Object.entries(CHARACTER_ARCHETYPES) as [CharacterArchetype, typeof CHARACTER_ARCHETYPES[CharacterArchetype]][]).map(([key, arch]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setSelectedArchetype(key);
                        setShowMediumDropdown(false);
                        // Reset style when archetype changes
                        setSelectedStyle(getStylesForArchetype(key)[0] || null);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center gap-2 ${selectedArchetype === key
                        ? 'bg-slate-blue/20 text-slate-blue-soft'
                        : 'text-white/80 hover:bg-graphite'
                        }`}
                    >
                      {selectedArchetype === key && <CheckIcon className="w-3 h-3" />}
                      <span className={selectedArchetype === key ? '' : 'pl-5'}>{arch.badge}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Input Area */}
          <div
            className={`relative rounded-2xl transition-all duration-300 ${uploadOnlyMode
                ? 'bg-carbon/50 opacity-50'
                : isFocused
                  ? 'bg-graphite ring-2 ring-slate-blue/50 shadow-lg shadow-slate-blue/10'
                  : 'bg-carbon hover:bg-neutral-800'
              }`}
          >
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder={uploadOnlyMode ? "Upload-only mode enabled - AI will analyze your images" : "e.g., female vampire, cyberpunk samurai, elven ranger..."}
              className="w-full h-32 bg-transparent rounded-2xl p-5 text-base text-white/90 focus:outline-none resize-none placeholder:text-gray-600"
              maxLength={500}
              disabled={uploadOnlyMode}
            />
            <div className="absolute bottom-3 right-4 flex items-center gap-3">
              <span className={`text-xs transition-colors ${description.length > 450 ? 'text-amber-400' : 'text-gray-600'
                }`}>
                {description.length}/500
              </span>
              {isAnalyzing && (
                <CircleNotchIcon className="w-4 h-4 text-slate-blue animate-spin" />
              )}
            </div>
          </div>

          {/* Suggested Tags */}
          {suggestedTags.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <SparkleIcon className="w-3 h-3 text-slate-blue" />
                Suggestions
              </span>
              <div className="flex flex-wrap gap-2">
                {suggestedTags.map((tag, index) => (
                  <button
                    key={index}
                    onClick={() => handleTagClick(tag)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-carbon hover:bg-graphite border border-iron/50 hover:border-slate-blue/50 rounded-full text-sm text-gray-400 hover:text-white transition-all active:scale-95"
                  >
                    <PlusIcon className="w-3 h-3 text-slate-blue" />
                    <span>{tag}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Reference Images Upload */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500 uppercase tracking-widest flex items-center gap-2">
                <ImageIcon className="w-3 h-3 text-slate-blue" />
                Reference Images
                <span className="text-gray-600 normal-case">(optional, up to 3)</span>
              </span>
              {referenceImages.length > 0 && (
                <span className="text-xs text-gray-600">{referenceImages.length}/3</span>
              )}
            </div>

            <div className="flex gap-3 items-start">
              {/* Uploaded Images */}
              {referenceImages.map((img) => (
                <div
                  key={img.id}
                  className="relative w-20 h-20 rounded-xl overflow-hidden bg-carbon border border-iron/50 group"
                >
                  <img
                    src={img.thumbnail || img.url}
                    alt="Reference"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(img.id)}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <XIcon className="w-3 h-3 text-white" />
                  </button>
                </div>
              ))}

              {/* Upload Button */}
              {referenceImages.length < 3 && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-iron/50 hover:border-slate-blue/50 bg-carbon hover:bg-graphite flex flex-col items-center justify-center cursor-pointer transition-all group"
                >
                  <UploadIcon className="w-5 h-5 text-gray-600 group-hover:text-slate-blue transition-colors" />
                  <span className="text-[10px] text-gray-600 group-hover:text-gray-500 mt-1">Upload</span>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageUpload(e.target.files)}
                className="hidden"
              />
            </div>

            {/* Upload-only mode toggle */}
            {referenceImages.length > 0 && (
              <div className="flex items-center justify-between py-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const newMode = !uploadOnlyMode;
                      setUploadOnlyMode(newMode);
                      // Clear description when enabling upload-only mode
                      if (newMode) {
                        setDescription('');
                      }
                    }}
                    className={`relative w-10 h-5 rounded-full transition-colors ${uploadOnlyMode ? 'bg-slate-blue' : 'bg-iron'
                      }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${uploadOnlyMode ? 'translate-x-5' : 'translate-x-0.5'
                        }`}
                    />
                  </button>
                  <span className="text-xs text-gray-400">
                    Upload only (skip description)
                  </span>
                </div>
                {isAnalyzingImages && (
                  <div className="flex items-center gap-2 text-slate-blue">
                    <CircleNotchIcon className="w-3 h-3 animate-spin" />
                    <span className="text-xs">Analyzing...</span>
                  </div>
                )}
              </div>
            )}

            {/* Image analysis result (shown in upload-only mode) */}
            {uploadOnlyMode && imageAnalysisResult && (
              <div className="bg-carbon rounded-xl p-3 border border-iron/30">
                <div className="flex items-center gap-2 mb-2">
                  <SparkleIcon className="w-3 h-3 text-slate-blue" />
                  <span className="text-xs text-slate-blue uppercase tracking-widest">AI Analysis</span>
                </div>
                <p className="text-sm text-dust mb-2">{imageAnalysisResult.description}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${imageAnalysisResult.gender === 'female'
                      ? 'bg-pink-500/20 text-pink-400'
                      : imageAnalysisResult.gender === 'male'
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                    {imageAnalysisResult.gender}
                  </span>
                  {imageAnalysisResult.characteristics.slice(0, 3).map((trait, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-graphite text-gray-400">
                      {trait}
                    </span>
                  ))}
                </div>
              </div>
            )}

          </div>



          {/* Style Carousel - Only show when medium is selected */}
          {selectedArchetype && (
            <div className="space-y-3 pt-4 border-t border-iron/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest text-gray-500">Style Refinement</span>
                  <span className="text-xs text-gray-600">({mediumMeta?.badge})</span>
                </div>
                {/* Carousel Navigation */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => scrollStyleCarousel('left')}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-graphite transition-all"
                  >
                    <CaretLeftIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => scrollStyleCarousel('right')}
                    className="p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-graphite transition-all"
                  >
                    <CaretRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Style Cards Carousel */}
              <div
                ref={styleCarouselRef}
                className="flex gap-3 overflow-x-auto py-1 px-1 -mx-1 pb-3 style-carousel"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#3F4448 #1E1E20',
                  WebkitOverflowScrolling: 'touch'
                }}
                onWheel={(e) => {
                  // Convert vertical scroll to horizontal
                  // Note: We don't call preventDefault() because React wheel events are passive
                  if (e.deltaY !== 0) {
                    // Use scrollLeft directly for instant, responsive scrolling
                    e.currentTarget.scrollLeft += e.deltaY * 1.5;
                  }
                }}
              >
                {availableStyles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedStyle(style)}
                    className={`flex-shrink-0 w-40 group relative rounded-xl overflow-hidden transition-all ${selectedStyle?.id === style.id
                      ? 'ring-2 ring-slate-blue ring-offset-2 ring-offset-onyx'
                      : 'hover:ring-1 hover:ring-iron'
                      }`}
                  >
                    {/* Preview Image or Gradient */}
                    <div
                      className="w-full aspect-square"
                      style={{
                        background: style.previewImage
                          ? `url(${style.previewImage}) center/cover`
                          : style.previewColor
                      }}
                    >
                      {/* Selected indicator */}
                      {selectedStyle?.id === style.id && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-slate-blue flex items-center justify-center">
                          <CheckIcon className="w-3 h-3 text-white" weight="bold" />
                        </div>
                      )}
                    </div>
                    {/* Style Name */}
                    <div className="p-3 bg-carbon">
                      <p className={`text-sm font-medium truncate ${selectedStyle?.id === style.id ? 'text-slate-blue-soft' : 'text-white/80'
                        }`}>
                        {style.name}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Selected Style Description */}
              {selectedStyle && (
                <div className="bg-carbon rounded-xl px-4 py-3 border border-iron/30">
                  <p className="text-xs text-gray-400 leading-relaxed">
                    <span className="text-slate-blue-soft font-medium">{selectedStyle.name}:</span>{' '}
                    {selectedStyle.description}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Bottom spacer to prevent content from being hidden behind fixed bar */}
          <div className="h-20" />
        </div>
      </div>

      {/* Fixed Bottom Action Bar - respects sidebar widths */}
      <div
        className="fixed bottom-0 bg-onyx/95 backdrop-blur-md border-t border-iron/30 px-6 py-4 z-50"
        style={{
          left: 'var(--sidebar-left, 0px)',
          right: 'var(--sidebar-right, 0px)',
        }}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          {/* Return to Characters Button */}
          {onBack ? (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-6 py-3 rounded-xl text-gray-500 hover:text-white hover:bg-graphite transition-all text-sm"
            >
              <UsersIcon className="w-4 h-4" />
              <span>Return to Characters</span>
            </button>
          ) : (
            <div />
          )}

          <div className="flex items-center gap-3">
            {/* Expand/Get Suggestions Button */}
            <button
              onClick={handleExpand}
              disabled={!description.trim() || isAnalyzing}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all ${description.trim() && !isAnalyzing
                ? 'bg-graphite hover:bg-iron text-white/80 hover:text-white active:scale-95'
                : 'bg-carbon text-gray-600 cursor-not-allowed'
                }`}
            >
              {isAnalyzing ? (
                <CircleNotchIcon className="w-4 h-4 animate-spin" />
              ) : (
                <SparkleIcon className="w-4 h-4" />
              )}
              <span>{analysis ? 'More Suggestions' : 'Get Suggestions'}</span>
              <CreditCostBadge cost={1} />
            </button>

            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              disabled={!canConfirm}
              className={`flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-medium transition-all ${canConfirm
                ? 'bg-gradient-to-r from-slate-blue to-[#9795FA] text-white shadow-lg shadow-slate-blue/30 hover:shadow-slate-blue/50 active:scale-95'
                : 'bg-carbon text-gray-600 cursor-not-allowed'
                }`}
              title={!selectedArchetype ? 'Please select a Medium first' : !analysis ? 'Click "Get Suggestions" first' : ''}
            >
              <span>Confirm</span>
              <CaretRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
